import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Envoyer à Formspree
      const response = await fetch('https://formspree.io/f/mwvnwnrk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message
        })
      });

      if (response.ok) {
        toast({
          title: "Message envoyé !",
          description: "Je vous répondrai dans les plus brefs délais.",
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer ou m'appeler directement.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className="text-5xl font-bold text-gray-900">
              Contactez-<span className="text-pink-600">moi</span>
            </h1>
            <video src="https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/nk3b9zei_logo-U2C.mp4" autoPlay loop muted playsInline className="h-24 w-auto flex-shrink-0 hidden sm:block rounded-lg" />
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une question ? Un projet ? N'hésitez pas à me contacter, je suis là pour vous aider
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Contact Cards */}
            <Card className="border-2 border-pink-100 hover:border-pink-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Phone className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Téléphone</h3>
                <a href="tel:0761926962" className="text-xl font-bold text-pink-600 hover:text-pink-700 transition-colors block">
                  07.61.92.69.62
                </a>
                <p className="text-xs text-gray-600 mt-2">Lun - Ven : 9h - 18h</p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  N'hésitez pas à me laisser un message, je vous rappelle dans les plus brefs délais
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-pink-100 hover:border-pink-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Mail className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                <a href="mailto:contact@urba2c.fr" className="text-base font-semibold text-pink-600 hover:text-pink-700 transition-colors block break-all">
                  contact@urba2c.fr
                </a>
                <p className="text-xs text-gray-600 mt-2">Réponse sous 48h</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-pink-100 hover:border-pink-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <MapPin className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Zone d'intervention</h3>
                <p className="text-gray-700 font-medium">
                  Secteur Charente
                </p>
                <p className="text-xs text-gray-600 mt-2">Déplacements possibles</p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-gray-200 shadow-xl">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Envoyez-moi un message</h2>
                  <p className="text-gray-600">Remplissez le formulaire ci-dessous et je vous recontacterai rapidement</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-700 font-medium">
                        Nom complet <span className="text-pink-600">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Votre nom"
                        className="border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700 font-medium">
                        Email <span className="text-pink-600">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre@email.com"
                        className="border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700 font-medium">
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        className="border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-gray-700 font-medium">
                        Sujet <span className="text-pink-600">*</span>
                      </Label>
                      <Select 
                        value={formData.subject} 
                        onValueChange={(value) => setFormData(prev => ({...prev, subject: value}))}
                        required
                      >
                        <SelectTrigger className="border-gray-300 focus:border-pink-500 focus:ring-pink-500">
                          <SelectValue placeholder="Choisissez un sujet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Permis de construire">Permis de construire</SelectItem>
                          <SelectItem value="Déclaration préalable">Déclaration préalable</SelectItem>
                          <SelectItem value="Certificat d'urbanisme">Certificat d'urbanisme</SelectItem>
                          <SelectItem value="Permis de démolir">Permis de démolir</SelectItem>
                          <SelectItem value="Permis d'aménager">Permis d'aménager</SelectItem>
                          <SelectItem value="Assainissement non collectif">Assainissement non collectif</SelectItem>
                          <SelectItem value="Autres">Autres</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-700 font-medium">
                      Message <span className="text-pink-600">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Décrivez votre projet ou votre demande..."
                      rows={6}
                      className="border-gray-300 focus:border-pink-500 focus:ring-pink-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white text-lg py-6 shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <span className="animate-spin mr-2">⏳</span>
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Send className="mr-2 w-5 h-5" />
                        Envoyer le message
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start space-x-4">
            <CheckCircle2 className="w-8 h-8 text-pink-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-2xl font-bold mb-3">Engagement qualité</h3>
              <p className="text-gray-300 leading-relaxed">
                Chaque demande reçoit une réponse personnalisée dans les 48 heures. 
                J'étudie votre projet avec attention pour vous proposer la meilleure solution adaptée à vos besoins.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;